import React, { useEffect } from 'react';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';

import { dateCellRenderer } from '@/pages/ContractDetails/ContractLines/grid/helper';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { validator } from './validation/validator';
import { CONTRACT_STATES_COLORS } from '@/config/statuses.config';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { CANCEL_TYPE } from '@/config/reasonTypes.config';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { Pill } from '@/components/common/Pill/Pill';
import { ContractLineBillingCycleOutput } from '@/graphql/contracts/_generated_/contracts';

export function useColumns(
  terminateContractLines: ContractLineBillingCycleOutput[]
): ColDef<ContractLineBillingCycleOutput>[] {
  const { getReasonCodes, isLoading, reasonCodes } = useGetReasonCodes();

  useEffect(() => {
    getReasonCodes(CANCEL_TYPE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCellRendererParams = ({ colDef, data, context }: ICellRendererParams) => ({
    errors: validator.getErrorsAt(colDef?.field!, data, { contract: context?.contract }),
    errorTitle: colDef!.headerName
  });

  return [
    {
      colId: 'status',
      headerName: 'Status',
      field: 'status',
      autoHeight: true,
      maxWidth: 140,
      suppressSizeToFit: true,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
      cellRenderer: ({ value }: ICellRendererParams<ContractLineBillingCycleOutput, 
      ContractLineBillingCycleOutput['status']>) => {
        const color = (value && CONTRACT_STATES_COLORS[value]) ?? CONTRACT_STATES_COLORS.Active;

        return (
          <Pill background={color} size="large">
            {value}
          </Pill>
        );
      },
    },
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'stockCode',
      initialWidth: 295,
      minWidth: 280,
      cellRenderer: ({ data }: ICellRendererParams<ContractLineBillingCycleOutput>) => {
        return `${data?.stockCode} - ${data?.stockName}`;
      }
    },
    {
      colId: 'reasonCode',
      headerName: 'Reason Name',
      field: 'statusChangeReason',
      initialWidth: 220,
      minWidth: 180,
      editable: true,
      cellRenderer: 'CellRenderer',
      cellEditor: 'DropdownEditor',
      cellEditorParams: ({ data }: ICellRendererParams<ContractLineBillingCycleOutput>) => {
        // To show old reason inside dropdown even if it was already disabled
        // (user can accidentally change reason and want to return back old one)
        const contractLine = terminateContractLines.find((cl) => cl.id === data?.id);
        const reasons = [...reasonCodes];
        const isOldReasonAlreadyInDropdown =
              Boolean(reasons.find((el) => el.code === contractLine?.statusChangeReason));
    
        if (!isOldReasonAlreadyInDropdown && contractLine?.statusChangeReason) {
          reasons.push({
            code: contractLine.statusChangeReason!,
            name: contractLine.statusChangeReasonName!,
            tenantID: '',
            enabled: true,
            __typename: 'ReasonCodeType'
          });
        }
    
        return {
          options: reasons,
          getOptionValue: (option: ReasonCode) => option.code,
          getOptionLabel: (option: ReasonCode) => option.name,
          saveOnChange: true,
          isLoading: isLoading
        };
      },
      valueFormatter: ({ value }: ValueFormatterParams<ContractLineBillingCycleOutput>) => {
        const reason = reasonCodes.find((r) => r.code === value);
        return reason ? reason.name : value || '';
      },
      valueSetter: (params: any) => {
        params.data.statusChangeReason = params.newValue.code.trim();
        return true;
      },
    },
    {
      colId: 'terminationDate',
      headerName: 'Termination Date',
      field: 'statusChangeDate',
      initialWidth: 120,
      minWidth: 120,
      editable: true,
      cellEditor: 'DatepickerEditor',
      cellRenderer: 'CellRenderer',
      cellRendererParams: getCellRendererParams,
      valueFormatter: ({ value }: ValueFormatterParams<ContractLineBillingCycleOutput>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      },
      cellEditorParams: () => ({
        saveOnChange: true,
      }),
    },
    {
      colId: 'comments',
      headerName: 'Comments',
      field: 'statusChangeComment',
      initialWidth: 140,
      minWidth: 140,
      editable: true,
      cellRenderer: 'CellRenderer',
      cellRendererParams: getCellRendererParams,
      valueSetter: (params: any) => {
        params.data.statusChangeComment = params.newValue.trim();
        return true;
      },
    },
    {
      colId: 'contractEndDate',
      headerName: 'Contract End Date',
      initialWidth: 150,
      minWidth: 150,
      cellRenderer: ({ context }: ICellRendererParams) => {
        const contractEndDate = (context.contract as Contract).contractEndDate;
        return contractEndDate ? dateCellRenderer({ value: contractEndDate }) : '';
      }
    },
    {
      colId: 'term',
      headerName: 'Contract Term',
      initialWidth: 125,
      minWidth: 125,
      cellRenderer: ({ context }: ICellRendererParams) => {
        const term = (context.contract as Contract).term;
        return term ? `${term} ${FormattingUtils.pluralize(term, 'month')}` : '';
      }
    }
  ];
}
